/**
 * UserGroup
 *
 * Represents the role/group of a user in the system.
 * - ADMINISTRATOR / PRODUCT_MANAGER / MANAGER: Internal admin users
 * - PARTNER: External partner users
 */
export enum UserGroup {
  ADMINISTRATOR = 'ADMINISTRATOR',
  PRODUCT_MANAGER = 'PRODUCT_MANAGER',
  MANAGER = 'MANAGER',
  PARTNER = 'PARTNER',
}

/**
 * Internal admin user groups (non-partner users).
 *
 * Used for:
 * - AdminRoute authorization
 * - Menu visibility control
 * - Feature-level permission checks
 */
export const ADMIN_USER_GROUPS = [
  UserGroup.ADMINISTRATOR,
  UserGroup.PRODUCT_MANAGER,
  UserGroup.MANAGER,
] as const;

/**
 * Union type of all internal admin groups.
 */
export type AdminUserGroup = typeof ADMIN_USER_GROUPS[number];

/**
 * Type guard: checks whether a user group is an internal admin group.
 */
export const isAdminUserGroup = (group: UserGroup): group is AdminUserGroup =>
  (ADMIN_USER_GROUPS as readonly UserGroup[]).includes(group);

/**
 * Type guard: checks whether a user group is a partner.
 */
export const isPartnerUserGroup = (
  group: UserGroup
): group is UserGroup.PARTNER => group === UserGroup.PARTNER;

/**
 * User account status.
 */
export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

/**
 * Base user model.
 *
 * Represents a user entity returned from most user-related APIs.
 */
export type User = {
  userId: number;
  username: string;
  name: string;
  group: UserGroup;
  status: UserStatus;
  position: string;
  email: string;
  phoneNumber: string;
  recentLoginTime?: string;
  partnerId?: number;
};

/**
 * Extended user model returned from `/users/me`.
 *
 * Includes feature flags that affect UI/behavior.
 */
export type UserResponse = User & {
  manualOrderEnabled: boolean;
};
